import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingController, ToastController } from '@ionic/angular';

import * as Sentry from '@sentry/browser';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import * as _ from 'lodash';
import { isNil } from 'lodash';
import { Evaluation360Record } from 'src/app/360-evaluation/_interfaces/360-evaluation-record.interface';
import { AreaConfig } from 'src/app/360-evaluation/_interfaces/area-config.interface';
import { ManagerEvaluationService } from '../manager-evaluation/manager-evaluation.service';
import { BaseClass } from '../shared/classes/base';
import { ProcessConfig } from '../shared/interfaces/process-config.interface';
import { ProcessStatusEnum, ProcessTypeEnum } from '../shared/interfaces/process.interface';
import { AuthService } from '../shared/services/auth.service';
import { TranslationService } from '../shared/services/translation.service';
import { FinalReport } from './_interfaces/final-report.interface';
import { FinalReportService } from './final-report.service';
import { ConfigService } from '../shared/services/multi-clients/config.service';


@Component({
    selector: 'app-final-report',
    templateUrl: './final-report.page.html',
    styleUrls: ['./final-report.page.scss']
})
export class FinalReportPage extends BaseClass implements OnDestroy, OnInit {

    public hideHireDate: boolean;
    public hideEvolutionOfEvaluationAndRankFromFinalReport: boolean;
    public divide360UsersLitByAreaEnabled: boolean;
    public nineboxEnabled: boolean;
    public disableBookButton: boolean;
    public showData = false;

    public report: FinalReport;
    public processConfig: ProcessConfig;
    public twoPlusTwoProcessConfig: ProcessConfig;
    public threeSixtyProcessConfig: ProcessConfig;
    public selfProcessConfig: ProcessConfig;
    public managerProcessConfig: ProcessConfig;
    public freeEvalMidYearProcessConfig: ProcessConfig;
    public freeEvalEndYearProcessConfig: ProcessConfig;
    public isPrinting: boolean = false;
    public hideOwnValuations: boolean = true;
    public previousYears: Array<number> = [
        new Date().getFullYear() - 3,
        new Date().getFullYear() - 2,
        new Date().getFullYear() - 1,
    ];
    public isImpersonate: boolean = false;
    public serviceDate: string;
    public hireDate: string;
    public areas: AreaConfig[];
    public evaluationByArea: { [key: string]: Array<Evaluation360Record> } = {};

    private readonly _userId: string;
    private _cycleId: number;
    private _returnPath: string = undefined;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _loadingController: LoadingController,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _reportService: FinalReportService,
        private _authService: AuthService,
        private _evaluationService: ManagerEvaluationService,
        private _configService: ConfigService
    ) {
        super(_toastController, _translationService);
        this._cycleId = parseInt(
            this._activatedRoute.snapshot.paramMap.get('cycleId'), 10
        );
        this._userId = this._activatedRoute.snapshot.paramMap.get('userId');
        const _impersonateUser = this._authService.getImpersonatedUser()
        const _loggedUser = this._authService.getLoggedUserId();
        this.isImpersonate = _impersonateUser !== null;
        this.hideOwnValuations = false;
    }

    ngOnInit() {
        this.hideHireDate = this._configService.settings.features.hideHireDate;
        this.hideEvolutionOfEvaluationAndRankFromFinalReport = this._configService.settings.features.hideEvolutionOfEvaluationAndRankFromFinalReport;
        this.divide360UsersLitByAreaEnabled = this._configService.settings.features.divide360UsersLitByArea;
        this.nineboxEnabled = this._configService.settings.features.nineBox.enabled;
        this.disableBookButton = this._configService.settings.features.disableBookButton;
    }

    ionViewWillEnter() {
        this._returnPath = localStorage.getItem('FinalReport_ReturnPath');
        this._loadReport();
    }

    public goBack(): void {
        this._router.navigate([
            this._returnPath ? this._returnPath : 'dashboard'
        ], {
            queryParams: { goBackState: true }
        });
    }

    public async print(): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        try {
            this.isPrinting = true;

            await new Promise(resolve => setTimeout(resolve, 300));

            document.querySelectorAll('#view-goal').forEach(el => el.remove());
            const wrapper = document.createElement('div');
            wrapper.id = 'PdfWrapper';
            wrapper.style.position = 'absolute';
            wrapper.style.top = '0';
            wrapper.style.left = '0';
            wrapper.style.width = '1000px';
            wrapper.style.zIndex = '-1';
            wrapper.style.backgroundColor = '#ffffff';

            const elementsToClone = [
                'PrintingHeader',
                'FreeEvalMid',
                'CoachReport',
                'Report360',
                'FreeEvalEnd',
                'SelfManager'
            ];
            const dpi = window.devicePixelRatio * 96;
            const pdf = new jsPDF('p', 'pt', 'a4');

            const pageHeightPx = 1434;
            console.log(`pageHeightPx= ${pageHeightPx}px`);
            let accumulatedHeight = 0;
            // Loop para clonar e adicionar os elementos
            for (const id of elementsToClone) {
                const original = document.getElementById(id);
                if (original) {
                    const clone = original.cloneNode(true) as HTMLElement;

                    // Estiliza o clone
                    // clone.querySelectorAll('ion-row, ion-card-header').forEach(row => {
                    //     const el = row as HTMLElement;
                    //     el.style.flexWrap = 'wrap';
                    //     el.style.minHeight = '10px';
                    //     el.style.overflow = 'visible';
                    // });

                    // Adiciona o clone ao wrapper
                    document.body.appendChild(wrapper);
                    wrapper.appendChild(clone);

                    await new Promise(resolve => setTimeout(resolve, 1000));

                    let cloneHeight = clone.offsetHeight;
                    let page = 1;
                    if (clone.offsetHeight > 0) {
                        let remainingHeight = pageHeightPx - (accumulatedHeight % pageHeightPx);

                        if (cloneHeight > remainingHeight) {
                            clone.querySelectorAll('ion-row, ion-card-header').forEach(element => {
                                const el = element as HTMLElement;
                                const elementHeight = el.getBoundingClientRect().height;
                                remainingHeight = pageHeightPx - accumulatedHeight;

                                if (elementHeight > remainingHeight) {
                                    const filler = document.createElement('div');
                                    filler.style.height = `${remainingHeight}px`;
                                    // filler.style.backgroundColor = 'red';

                                    el.parentElement.insertBefore(filler, el);
                                    accumulatedHeight += remainingHeight;
                                    console.log(`adicionando remainingHeight de ${remainingHeight}px antes de ${el.tagName} em ${id}`);
                                    accumulatedHeight = elementHeight;
                                    console.log(`acumulado ${accumulatedHeight}px`);
                                    page++;
                                } else {
                                    accumulatedHeight += elementHeight;
                                    console.log(`${el.tagName} de ${elementHeight}px em ${id}, acumulado ${accumulatedHeight}px`);
                                }
                            });
                        } else {
                            accumulatedHeight += cloneHeight;
                        }
                        console.log(`cloneHeight = ${cloneHeight}px em ${id}, acumulado = ${accumulatedHeight}px`);
                        console.log(clone);
                    }
                }
            }

            // Gera a imagem do conteúdo
            document.body.appendChild(wrapper);

            // Evita que ion-row seja cortado entre páginas
            const style = document.createElement('style');
            style.innerHTML = `
                            #PdfWrapper ion-row {
                                page-break-inside: avoid !important;
                                break-inside: avoid !important;
                            }
                            `;
            wrapper.appendChild(style);

            await new Promise(resolve => setTimeout(resolve, 300));

            const canvas = await html2canvas(wrapper, {
                scale: 2,
                useCORS: true,
                scrollY: -window.scrollY
            });

            const imgData = canvas.toDataURL('image/png');

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            console.log(`pageHeight= ${pageHeight}px`);
            const imgHeight = (canvas.height * pageWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
            heightLeft -= pageHeight;

            // Continua adicionando páginas até que o conteúdo caiba completamente
            while (heightLeft > 0) {
                position -= pageHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight, undefined, 'FAST');
                heightLeft -= pageHeight;
            }

            // Define o nome do relatório e salva o PDF
            let reportName = this._translationService.getTranslation('calibration.fullReport')
                + ' - '
                + this.report.cycle.name
                + ' - '
                + this.report.user.name
                    .replace(/[\/\\?%*:|"<>]/g, '')
                    .replace(/\s+/g, '_');
            pdf.save(reportName + '.pdf');

            // Remove o wrapper após a impressão
            document.body.removeChild(wrapper);
            this.isPrinting = false;
            await loading.dismiss();
        } catch (error) {
            console.error(error);
            await loading.dismiss();
            this.isPrinting = false;

            Sentry.captureException((error as any).originalError || error);
            await this.notify(
                this._translationService.getTranslation('generic.printError'),
                'danger'
            );
        }
    }

    public getPreviousYearStat(year: number, stat: string): string {
        if (!this.report || !this.report.user || !this.report.user.previousEvaluations || !this.report.user.previousRankYearStats)
            return '--';

        if (stat === 'evaluation') {
            const valueForTheYear = this.report.user.previousEvaluations.find(ev => ev.year === year);
            return valueForTheYear ? valueForTheYear.value : '--';
        } else {
            const valueForTheYear = this.report.user.previousRankYearStats.find(ev => ev.year === year);
            return valueForTheYear ? valueForTheYear.value : '--';
        }
    }

    public getProcessConfig(
        processConfigs: Array<ProcessConfig>,
        type: ProcessTypeEnum
    ): ProcessConfig {
        return processConfigs.find(c => c.type === type);
    }

    public processIsConfigured(
        processConfigs: Array<ProcessConfig>,
        type: ProcessTypeEnum
    ): boolean {
        return processConfigs.some(c => c.type === type);
    }

    private _loadReport(): void {
        this._reportService.getUserCycleReport(
            this._userId, this._cycleId
        ).subscribe((response) => {
            this.report = response.data;
            this.showData = this.report.cycleProcessConfigStatus === ProcessStatusEnum.AllowedViewing || this.report.isManager;
            this.twoPlusTwoProcessConfig = this.report.processConfigs.find(c => c.type === ProcessTypeEnum.TwoPlusTwoCoaching);
            this.threeSixtyProcessConfig = this.report.processConfigs.find(c => c.type === ProcessTypeEnum.ThreeSixtyEvaluation);
            this.freeEvalEndYearProcessConfig = this.report.processConfigs.find(c => c.type === ProcessTypeEnum.FreeEvaluationEndYear);
            this.freeEvalMidYearProcessConfig = this.report.processConfigs.find(c => c.type === ProcessTypeEnum.FreeEvaluationMidYear);
            this.managerProcessConfig = this.report.processConfigs.find(c => c.type === ProcessTypeEnum.ManagerEvaluation);
            this.hireDate = this._getLocalFormatDate(this.report.user.hireDate);
            this.serviceDate = this._getLocalFormatDate(this.report.user.serviceDate);
            this.previousYears = [
                this.report.cycle.year - 3,
                this.report.cycle.year - 2,
                this.report.cycle.year - 1,
            ];
            if (this.divide360UsersLitByAreaEnabled) {
                this.areas = this.report.processConfigs.find(c => c.type === ProcessTypeEnum.ThreeSixtyEvaluatorsSelection).areas;
                this.evaluationByArea = _.groupBy(response.data.threeSixtyEvaluations, 'areaId');
            }
        }, (error) => this.notifyError(error));
    }

    public async getTeamComparisonExcel() {
        let onlyCommentsColumns = true;
        await this._evaluationService.getTeamComparisonExcel(
            this._cycleId, onlyCommentsColumns, this._userId
        ).subscribe(
            async (data) =>
                await this.notify(
                    this._translationService.getTranslation('Report.Success'),
                    'success'
                ),
            async (error) => {
                if (error.error instanceof Blob) {
                    const reader = new FileReader();
                    reader.onload = async () => {
                        const blobText = reader.result;
                        let err = JSON.parse(blobText.toString());
                        err.status = !isNil(error.status) ? error.status : 400;
                        await this.notifyError(err);
                    };
                    reader.readAsText(error.error);
                } else
                    await this.notifyError(error);
            }
        );
    }

    private _getLocalFormatDate(date: string): string {
        if (isNil(date)) return null;

        let [day, month, year] = date.split("/");
        let language = this._translationService.getSelectedLanguage();

        if (language === 'en-US') {
            return `${month}/${day}/${year}`;
        }
        return `${day}/${month}/${year}`;
    }

    @HostListener('window:beforeunload')
    ngOnDestroy() {
        localStorage.removeItem('FinalReport_ReturnPath');
    }

}

